import { ReactInputEventHandler } from 'lynkco-up-core/dist/types/types';
import { userRoles } from '../../Users/utils';
import { User } from '../../../services/users/types';
import { useUserContext } from '../../../modules/Users/UserContext';

type UserToEditProp = {
  userToEdit: User | null;
  setUserToEdit: React.Dispatch<React.SetStateAction<User | null>>;
};

function UserGroup({ userToEdit, setUserToEdit }: UserToEditProp) {
  const { user, checkAccess } = useUserContext();
  const isUserToEditAdmin = userToEdit?.roles.includes('administrator');
  const isLoggedInUserAdmin = user.roles.includes('administrator');

  const hasAccess = (roleValue: string) => checkAccess(`FUNCTION_ASSIGN_ROLE_${roleValue.toUpperCase()}`);

  const isChecked = (value: string) => {
    return userToEdit?.roles.includes(value) || false;
  };

  const handleRoleChange: ReactInputEventHandler = e => {
    const { value, checked, name } = e.target as HTMLInputElement;

    if (!userToEdit) return;

    let updatedRoles: string[] = [];

    if (value === 'administrator' && checked) {
      updatedRoles = ['administrator'];
    } else if (value === 'administrator' && !checked) {
      updatedRoles = userToEdit.roles.filter(role => role !== 'administrator');
    } else {
      updatedRoles = userToEdit.roles.filter(role => {
        const roleObj = userRoles.find(r => r.value === role);
        return roleObj?.name !== name;
      });

      if (checked) {
        updatedRoles.push(value);
      }
    }

    setUserToEdit({ ...userToEdit, roles: updatedRoles });
  };

  return (
    <div className="mt-3">
      {/* Admin Group */}
      <div className="mb-4">
        <p className={isLoggedInUserAdmin ? 'font-semibold text-base mb-2' : 'opacity-0'}>Admin</p>
        {userRoles
          .filter(role => role.name === 'admin' && hasAccess(role.value))
          .map(role => (
            <div key={role.value} className="flex justify-start items-center py-1">
              <input
                className="focus:ring-primary-500 text-primary-600"
                name={role.name}
                id={role.value}
                value={role.value}
                type="checkbox"
                onChange={handleRoleChange}
                checked={isChecked(role.value)}
              />
              <label className="ml-3" htmlFor={role.value}>
                {role.label}
              </label>
            </div>
          ))}
      </div>

      {/* Carsharing Group */}
      <div className="mb-4">
        <p
          className={
            isLoggedInUserAdmin || user.roles.includes('teamlead') ? 'font-semibold text-base mb-2' : 'opacity-0'
          }>
          Carsharing
        </p>
        {userRoles
          .filter(role => role.name === 'carsharing' && hasAccess(role.value))
          .map(role => (
            <div key={role.value} className="flex justify-start items-center py-1">
              <input
                className={isUserToEditAdmin ? 'opacity-50' : 'focus:ring-primary-500 text-primary-600'}
                name={role.name}
                id={role.value}
                value={role.value}
                type="checkbox"
                onChange={handleRoleChange}
                checked={isChecked(role.value)}
                disabled={isUserToEditAdmin}
              />
              <label className={isUserToEditAdmin ? 'ml-3 opacity-50' : 'ml-3'} htmlFor={role.value}>
                {role.label}
              </label>
            </div>
          ))}
      </div>

      {/* Colab Group */}
      <div className="mb-4">
        <p
          className={
            isLoggedInUserAdmin || user.roles.includes('colabAdmin') ? 'font-semibold text-base mb-2' : 'opacity-0'
          }>
          Colab
        </p>
        {userRoles
          .filter(role => role.name === 'colab' && hasAccess(role.value))
          .map(role => (
            <div key={role.value} className="flex justify-start items-center py-1">
              <input
                className={isUserToEditAdmin ? 'opacity-50' : 'focus:ring-primary-500 text-primary-600'}
                name={role.name}
                id={role.value}
                value={role.value}
                type="checkbox"
                onChange={handleRoleChange}
                checked={isChecked(role.value)}
                disabled={isUserToEditAdmin}
              />
              <label className={isUserToEditAdmin ? 'ml-3 opacity-50' : 'ml-3'} htmlFor={role.value}>
                {role.label}
              </label>
            </div>
          ))}
      </div>

      {/* Connectivity Group */}
      <div className="mb-4">
        <p className={isLoggedInUserAdmin ? 'font-semibold text-base mb-2' : 'opacity-0'}>Connectivity</p>
        {userRoles
          .filter(role => role.name === 'connectivity' && hasAccess(role.value))
          .map(role => (
            <div key={role.value} className="flex justify-start items-center py-1">
              <input
                className="focus:ring-primary-500 text-primary-600 opacity-50"
                name={role.name}
                id={role.value}
                value={role.value}
                type="checkbox"
                onChange={handleRoleChange}
                checked={isChecked(role.value)}
                disabled={true}
              />
              <label className="ml-3 opacity-50" htmlFor={role.value}>
                {role.label}
              </label>
            </div>
          ))}
      </div>

      {/* Update Management Group */}
      <div className="mb-4">
        <p
          className={
            isLoggedInUserAdmin || user.roles.includes('updateManagementAdmin')
              ? 'font-semibold text-base mb-2'
              : 'opacity-0'
          }>
          Update Management
        </p>
        {userRoles
          .filter(role => role.name === 'updateManagement' && hasAccess(role.value))
          .map(role => (
            <div key={role.value} className="flex justify-start items-center py-1">
              <input
                className={isUserToEditAdmin ? 'opacity-50' : 'focus:ring-primary-500 text-primary-600'}
                name={role.name}
                id={role.value}
                value={role.value}
                type="checkbox"
                onChange={handleRoleChange}
                checked={isChecked(role.value)}
                disabled={isUserToEditAdmin}
              />
              <label className={isUserToEditAdmin ? 'ml-3 opacity-50' : 'ml-3'} htmlFor={role.value}>
                {role.label}
              </label>
            </div>
          ))}
      </div>

      {/* Dealership Management Group */}
      <div className="mb-4">
        <p
          className={
            isLoggedInUserAdmin || user.permissions.includes('DEALERSHIP_USER')
              ? 'font-semibold text-base mb-2'
              : 'opacity-0'
          }>
          Dealership Management
        </p>
        {userRoles
          .filter(role => role.name === 'dealershipuser' && hasAccess(role.value))
          .map(role => (
            <div key={role.value} className="flex justify-start items-center py-1">
              <input
                className={isUserToEditAdmin ? 'opacity-50' : 'focus:ring-primary-500 text-primary-600'}
                name={role.name}
                id={role.value}
                value={role.value}
                type="checkbox"
                onChange={handleRoleChange}
                checked={isChecked(role.value)}
                disabled={isUserToEditAdmin}
              />
              <label className={isUserToEditAdmin ? 'ml-3 opacity-50' : 'ml-3'} htmlFor={role.value}>
                {role.label}
              </label>
            </div>
          ))}
      </div>
    </div>
  );
}
export default UserGroup;
